'use client'
import { PropsWithChildren, useState, useEffect } from 'react'
import MainNavigation from '@/Components/nav/MainNavigation'
import Footer from '@/Components/custom/footer'
import BreakpointIndicator from '@/Components/experiments/breakpoint-indicator'
import BackToTop from '@/Components/custom/back-to-top'
import { Toaster } from '@/Components/ui/toaster'
import { MyRoshanCTA } from '@/Components/sections/MyRoshanCTA'
import { motion, MotionConfig } from 'framer-motion'
import { usePage } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { Head } from '@inertiajs/react'
import { PageProps } from '@/types'

export default function Layout({ children }: PropsWithChildren) {
  const page = usePage().component
  const { theme, seo, canonical } = usePage<PageProps>().props
  const isError = page === 'Error'
  const title = seo?.title ?? ''

  return (
    <MotionConfig reducedMotion="user">
      <div suppressHydrationWarning className={theme === 'business' ? 'business-theme' : ''}>
        {/* What search engines and shared links read. An error is not a page to list. */}
        <Head title={title}>
          {seo?.description ? (
            <meta head-key="description" name="description" content={seo.description} />
          ) : null}
          {isError ? (
            <meta head-key="robots" name="robots" content="noindex" />
          ) : (
            <link head-key="canonical" rel="canonical" href={canonical} />
          )}
          <meta head-key="og:site_name" property="og:site_name" content="Roshan" />
          <meta head-key="og:type" property="og:type" content="website" />
          <meta head-key="og:url" property="og:url" content={canonical} />
          {title ? <meta head-key="og:title" property="og:title" content={title} /> : null}
          {seo?.description ? (
            <meta head-key="og:description" property="og:description" content={seo.description} />
          ) : null}
          <meta head-key="twitter:card" name="twitter:card" content="summary" />
          {(seo?.schema ?? []).map((item, index) => (
            <script
              key={index}
              head-key={`schema-${index}`}
              type="application/ld+json"
              dangerouslySetInnerHTML={{ __html: JSON.stringify(item).replace(/</g, '\\u003c') }}
            />
          ))}
        </Head>
        {/* Left out, not hidden: a hidden one would still run its animation. */}
        {['News/Show', 'VipCard', 'Error'].includes(page) ? null : (
          <motion.div
            className={cn(
              'absolute top-[100px] mx-auto h-[200px] w-[200px] min-w-fit max-w-7xl rounded-full bg-gradient-to-t from-primary to-business-blue blur-[140px] [--left-end:calc(100%-360px)] [--left-start:20px] md:top-[180px] md:h-[288px] md:w-[320px] md:[--left-end:calc(100%-365px)] md:[--left-start:45px] lg:top-[220px] lg:h-[360px] lg:w-[400px] lg:[--left-end:calc(100%-500px)] lg:[--left-start:140px]',
              'backface-visibility-hidden transform-gpu'
            )}
            animate={{
              left: ['var(--left-start)', 'var(--left-end)'],
              rotate: [-90, 0, 90, 0, -90],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: 'mirror',
              ease: 'linear', // Linear for continuous animations
            }}
            style={{
              willChange: 'transform',
              backfaceVisibility: 'hidden',
              filter: 'blur(140px)',
            }}
          ></motion.div>
        )}

        <MainNavigation />

        <main className="relative mx-auto mt-4 flex max-w-7xl flex-col gap-10 px-4 md:mt-8 md:gap-16 md:px-8 lg:mt-12 lg:px-[100px]">
          {children}
          <Toaster />
        </main>

        <MyRoshanCTA />

        <Footer />
        {/* <BreakpointIndicator /> */}
        <BackToTop />
      </div>
    </MotionConfig>
  )
}
